import React, { PropsWithChildren } from 'react';

import { AddContextualData } from '@opendoor/observability';

import { TeamError, UnauthenticatedError, UnauthorizedError, UserError } from './Error';

export const OD_TEAM_BOUNDARY_ERROR = 'TeamErrorBoundary';

export type TEAM =
  | 'seller-engineering'
  | 'growth'
  | 'consumer-platform'
  | 'marketplace'
  // NOTE: No alerts have been set up in kennel for this team yet
  | 'listings';

export interface ODTeamBoundaryProps extends PropsWithChildren {
  team: TEAM;
}

/**
 * Implements componentDidCatch to track the error in observability with team data but does not
 * render a fallback. That's still the responsibility of the app-wide ErrorBoundary.
 * @throws TeamError
 */
export class TeamErrorBoundary extends React.Component<
  ODTeamBoundaryProps,
  { hasError?: boolean; error?: Error }
> {
  /** Calls super */
  constructor(props: ODTeamBoundaryProps) {
    super(props);
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  /** @throws OD Team Boundary Error */
  componentDidCatch(error: Error | TeamError) {
    // Just forward information error has already been caught once by a TeamErrorBoundary
    if (error instanceof TeamError) {
      throw error;
    }

    if (error instanceof UserError) {
      throw new TeamError(error, this.props.team, 'user-error');
    }

    // Append team to authorization and authentication errors and re-throw
    if (error instanceof UnauthenticatedError) {
      throw new UnauthenticatedError(error.triggeredQuery, 'app-code', this.props.team);
    } else if (error.message.includes('Attempted to query while unauthenticated')) {
      throw new UnauthenticatedError(undefined, 'app-code', this.props.team);
    } else if (error instanceof UnauthorizedError) {
      throw new UnauthorizedError(
        error.triggeredQuery,
        error.sellDirectOfferAccessError,
        this.props.team,
      );
    }

    // Falls back to system error unless otherwise specified
    throw new TeamError(error, this.props.team, 'system-error');
  }

  render() {
    return this.props.children;
  }
}

/**
 * ODTeamBoundary indicates that a section of the component tree belongs to a specific Opendoor team.
 * Errors that occur below this boundary -- both uncaught errors and errors tracked manually with the
 * trackError function from observability -- will be automatically tagged with the name of the team.
 */
export const ODTeamBoundary: React.FC<ODTeamBoundaryProps> = (props) => {
  return (
    <AddContextualData data={{ team: props.team }}>
      <TeamErrorBoundary {...props} />
    </AddContextualData>
  );
};
